import { Button, Card, Container, Divider, Group, Stack, Text, Title } from '@mantine/core'
import { useParams } from 'react-router-dom'
import Money from '../../components/shared/Money'
import { ScheduleTable } from '../../components/shared/Pieces'
import { useResource } from '../../hooks'
import Message from '../../i18n/Message'
import { keys, loadAgreement, loadLoan, loadReceipt } from '../../store/dataActionCreators'
import { formatDate, formatDateTime } from '../../utils/format'
import '../../print.css'

function Sheet({ company, title, children }) {
  return (
    <Container size={760} py="lg">
      <Group justify="flex-end" mb="md" className="no-print">
        <Button onClick={() => window.print()}><Message id="print" /></Button>
      </Group>
      <Card className="print-sheet" p="xl">
        <Stack gap="md">
          <div>
            <Title order={3}>{company?.name || 'United Finance Africa Limited'}</Title>
            <Text c="dimmed" size="sm">{[company?.address, company?.phone, company?.email].filter(Boolean).join(' · ')}</Text>
          </div>
          <Title order={2}><Message id={title} /></Title>
          <Divider />
          {children}
        </Stack>
      </Card>
    </Container>
  )
}

const Row = ({ label, children }) => (
  <Group justify="space-between" wrap="nowrap" align="flex-start">
    <Text size="sm" c="dimmed"><Message id={label} /></Text>
    <Text size="sm" ta="right" component="div">{children}</Text>
  </Group>
)

export function PrintReceipt() {
  const { id } = useParams()
  const { data } = useResource(keys.receipt(id), () => loadReceipt(id))
  if (!data) return null
  const { receipt, payment, customer, company } = data
  const s = receipt.snapshot
  return (
    <Sheet company={company} title="receipt_title">
      <Row label="col_ref"><Text ff="monospace" span>{receipt.receipt_no}</Text></Row>
      <Row label="col_date">{formatDateTime(receipt.issued_at)}</Row>
      <Row label="col_name">{customer.first_name} {customer.last_name} ({customer.customer_no})</Row>
      <Row label="col_loan"><Text ff="monospace" span>{s.loanNo}</Text></Row>
      <Row label="col_type"><Message id={`kind_${payment.kind}`} /> · <Message id={`method_${payment.method}`} /></Row>
      <Row label="col_amount"><Money kobo={s.amountKobo} fw={600} /></Row>
      <Row label="receipt_balance"><Money kobo={s.balanceAfterKobo} /></Row>
    </Sheet>
  )
}

export function PrintStatement() {
  const { loanId } = useParams()
  const { data } = useResource(keys.loan(loanId), () => loadLoan(loanId))
  if (!data) return null
  const { loan, installments, payments, company } = data
  return (
    <Sheet company={company} title="statement_title">
      <Row label="col_ref"><Text ff="monospace" span>{loan.loan_no}</Text></Row>
      <Row label="col_name">{loan.customer_name} ({loan.customer_no})</Row>
      <Row label="quote_total"><Money kobo={loan.total_kobo} /></Row>
      <Row label="col_paid"><Money kobo={loan.paid_kobo} /></Row>
      <Row label="col_outstanding"><Money kobo={loan.outstanding_kobo} fw={600} /></Row>
      <ScheduleTable installments={installments} />
      <Title order={5}><Message id="payments_title" /></Title>
      {payments.filter((p) => p.status === 'verified').map((p) => (
        <Row key={p.id} label="col_date">{formatDate(p.paid_at)} · <Message id={`kind_${p.kind}`} /> · <Money kobo={p.amount_kobo} /></Row>
      ))}
    </Sheet>
  )
}

export function PrintAgreement() {
  const { applicationId } = useParams()
  const { data } = useResource(keys.agreement(applicationId), () => loadAgreement(applicationId))
  if (!data) return null
  const a = data.application
  return (
    <Sheet company={data.company} title="agreement_title">
      <Row label="col_ref"><Text ff="monospace" span>{a.application_no}</Text></Row>
      <Row label="col_name">{data.customer.name} ({data.customer.customer_no})</Row>
      <Row label="quote_financed"><Money kobo={a.principal_kobo} /></Row>
      <Row label="quote_interest"><Money kobo={a.interest_kobo} /></Row>
      <Row label="quote_total"><Money kobo={a.total_repayable_kobo} fw={600} /></Row>
      <Row label="field_term"><Message id="term_months" values={{ count: a.term_months }} /></Row>
      <Divider />
      <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{data.text}</Text>
      <Text size="xs" c="dimmed">v{data.version} · <Text span ff="monospace">{data.hash.slice(0, 16)}</Text></Text>
    </Sheet>
  )
}
